import React, {memo, useEffect, useState} from 'react';
import {DataTableRequest} from "../../types/Requests/API/DataTableRequest";
import {Button} from "@material-tailwind/react";
import FooterMenu from "../Sidebar/FooterMenu";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import P from "../P";
import Loader from "../../assets/icons/Loader";
import {useTranslation} from "react-i18next";
import {SearchWithFilters} from "./SearchWithFilters";
import RecordsIcon from "../../assets/icons/RecordsIcon";
import {Tooltip} from "@mui/material";

export interface HeaderInterface {
    name: string,
    element?: JSX.Element,
    className?: string,
    sortBy?: string,
    legalSortBy?: string;
}

interface DataTableProps {
    headers: HeaderInterface[],
    search?: string,
    length: number,
    page: number,
    sort_by: string,
    sort_direction: 0 | 1,
    filters?: JSX.Element,
    extraFilters?: JSX.Element,
    fullWidthFilters?: JSX.Element,
    children: JSX.Element,
    onChange: (request: DataTableRequest) => void,
    nodata: boolean,
    total_pages: number,
    count: number,
    inProgress?: boolean,
    rightPanel?: JSX.Element,
    options?: {
        searching?: boolean,
        sorting?: boolean,
        length?: boolean,
    }
}

const DataTable = ({
                       headers,
                       children,
                       onChange,
                       search,
                       length,
                       page,
                       sort_direction,
                       sort_by,
                       filters,
                       extraFilters,
                       fullWidthFilters,
                       rightPanel,
                       nodata, total_pages, count,
                       options = {
                           searching: true,
                           sorting: true,
                           length: true,
                       },
                       inProgress = false
                   }: DataTableProps) => {

    const {t} = useTranslation();
    const allowedLengths = [{name: 10}, {name: 25}, {name: 50}, {name: 100}, {name: 500}, {name: 1000}];
    const [pageInputValue, setPageInputValue] = useState(page.toString());

    useEffect(() => {
        setPageInputValue(page.toString());
    }, [page, total_pages]);

    const clampPageValue = (value: number) => {
        if (total_pages < 1) {
            return 1;
        }
        if (value < 1) {
            return 1;
        }
        if (value > total_pages) {
            return total_pages;
        }
        return value;
    }

    const onPageInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const numericOnly = event.target.value.replace(/[^0-9]/g, '');
        setPageInputValue(numericOnly);
    }

    const onPageInputBlur = () => {
        if (pageInputValue === '') {
            setPageInputValue(page.toString());
            return;
        }
        const parsedValue = parseInt(pageInputValue, 10);
        if (Number.isNaN(parsedValue)) {
            setPageInputValue(page.toString());
            return;
        }
        const clampedValue = clampPageValue(parsedValue);
        if (clampedValue !== parsedValue) {
            setPageInputValue(clampedValue.toString());
        }
    }

    const onSubmitPageChange = () => {
        const parsedValue = parseInt(pageInputValue, 10);
        if (Number.isNaN(parsedValue)) {
            setPageInputValue(page.toString());
            return;
        }
        const clampedValue = clampPageValue(parsedValue);
        setPageInputValue(clampedValue.toString());
        if (clampedValue !== page) {
            onPageChanged(clampedValue);
        }
    }

    const parsedPageInputValue = pageInputValue === '' ? Number.NaN : parseInt(pageInputValue, 10);
    const shouldShowSubmit = !Number.isNaN(parsedPageInputValue) && parsedPageInputValue !== page;
    const showPaginationControls = total_pages > 1;
    const disableFirst = page === 1 || inProgress;
    const disablePrev = page === 1 || inProgress;
    const disableNext = page === total_pages || inProgress;
    const disableLast = page === total_pages || inProgress;
    const shouldRenderFirst = page > 1 && showPaginationControls && page !== 2;
    const shouldRenderLast = showPaginationControls && page !== total_pages - 1;

    const toRequest = (o: object): DataTableRequest => {
        return {
            search: search,
            length: length,
            page: page,
            sort_direction: sort_direction,
            sort_by: sort_by,
            ...o
        }
    }
    const onSearchChanged = (e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            onChange(toRequest({page: 1, search: e.currentTarget.value ?? ''}));
        }
    }

    const onLengthChanged = (s: string | number) => {
        onChange(toRequest({length: s, page: 1}));
    }

    const onPageChanged = (p: number) => {
        inProgress = true;
        onChange(toRequest({page: p}));
    }

    const onChangeSort = (o: boolean, f: string) => {
        inProgress = true;
        onChange(toRequest({sort_direction: o ? 1 : 0, sort_by: f}));
    }

    const extraClasses = 'border-blue-gray-100 bg-blue-gray-50/50 border-y dark:border-blue-gray-800 dark:bg-gray-900 p-4'
    const pagination = <div
        className="flex items-center flex-col md:flex-row gap-4 md:gap-0 justify-between border-t border-blue-gray-50 dark:border-blue-gray-800 p-4">
        <div className="hidden md:inline">
            <P>
                {t('admin.datatable.page', {page, total_pages})}
            </P>
        </div>
        <P><>
            <span
                className="hidden md:inline">{total_pages === 1 ? t('admin.datatable.total_records', {count}) : t('admin.datatable.shown_records', {
                count,
                number: page * length > count ? count : page * length
            })} </span>
            <span
                className="md:hidden">{total_pages === 1 ? t('admin.datatable.total_records', {count}) : t('admin.datatable.shown_records_mobile', {
                count,
                number: page * length > count ? count : page * length
            })}</span>
        </>
        </P>
        {showPaginationControls ?
            <div className="flex items-center gap-2">
                {shouldRenderFirst &&
                    <Button variant="text" size="sm"
                            className="px-3 py-2 font-medium text-blue-gray-900 dark:text-white normal-case"
                            disabled={disableFirst}
                            aria-label={t('admin.datatable.first')}
                            title={t('admin.datatable.first')}
                            onClick={() => onPageChanged(1)}>
                        <span className="hidden md:inline">{t('admin.datatable.first')}</span>
                        <FontAwesomeIcon className="md:hidden" icon={solid("angles-left")}/>
                    </Button>}
                {!disablePrev ? <Button size="sm"
                                        className="px-5 py-2 text-white bg-black min-w-[auto] flex items-center justify-center"
                                        disabled={disablePrev}
                                        aria-label={t('admin.datatable.prev')}
                                        title={t('admin.datatable.prev')}
                                        onClick={() => onPageChanged(page - 1)}>
                    <FontAwesomeIcon icon={solid("angle-left")}/>
                </Button> : ''}
                <div
                    className="flex items-stretch rounded-md border border-blue-gray-100 dark:border-blue-gray-700 overflow-hidden">
                    <input
                        type="number"
                        min={1}
                        max={total_pages}
                        step={1}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={pageInputValue}
                        onChange={onPageInputChange}
                        onBlur={onPageInputBlur}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && shouldShowSubmit && !inProgress) {
                                event.preventDefault();
                                onSubmitPageChange();
                            }
                        }}
                        className="w-auto px-3 py-1 text-center text-sm text-blue-gray-900 dark:text-white bg-transparent outline-none hide-number-input-arrows"
                    />
                    <span
                        className={`px-3 py-1 text-sm bg-blue-gray-50 text-blue-gray-700 border-l border-blue-gray-100 dark:bg-gray-900 dark:text-gray-300 dark:border-blue-gray-700 whitespace-nowrap select-none ${shouldShowSubmit ? 'hidden md:block' : ''}`}>
                        {t('admin.datatable.of_total_pages', {total_pages})}
                    </span>
                    {shouldShowSubmit &&
                        <button
                            type="button"
                            className="px-3 py-1 text-sm font-semibold text-white bg-teal-500 whitespace-nowrap"
                            disabled={inProgress}
                            onClick={onSubmitPageChange}
                        >
                            <span className="hidden md:inline">{t('admin.datatable.submit')}</span>
                            <FontAwesomeIcon className="md:hidden" icon={solid("magnifying-glass")}/>
                        </button>}
                </div>
                {!disableNext ? <Button variant="outlined" size="sm"
                                        className="px-5 py-2 text-white bg-black min-w-[auto] flex items-center justify-center"
                                        disabled={disableNext}
                                        aria-label={t('admin.datatable.next')}
                                        title={t('admin.datatable.next')}
                                        onClick={() => onPageChanged(page + 1)}>
                    <FontAwesomeIcon icon={solid("angle-right")}/>
                </Button> : ''}
                {shouldRenderLast &&
                    <Button variant="text" size="sm"
                            className="px-3 py-2 font-medium text-blue-gray-900 dark:text-white normal-case"
                            disabled={disableLast}
                            aria-label={t('admin.datatable.last')}
                            title={t('admin.datatable.last')}
                            onClick={() => onPageChanged(total_pages)}>
                        <span className="hidden md:inline">{t('admin.datatable.last')}</span>
                        <FontAwesomeIcon className="md:hidden" icon={solid("angles-right")}/>
                    </Button>}
            </div> : null}
    </div>

    return <div>

        <div className="grid grid-cols-1 lg:grid-cols-3 mb-12">
            <div className="col-span-2 flex items-center flex-row gap-2 relative">
                {options?.searching &&
                    <SearchWithFilters search={search ?? ''} onSearchChanged={onSearchChanged} filters={filters}/>}
                <FooterMenu icon={
                    <Tooltip title={t('admin.datatable.show_records')}>
                        <RecordsIcon className="fill-white text-white w-5 h-5"/>
                    </Tooltip>
                } onClick={onLengthChanged}
                            selected={length}
                            list={allowedLengths}
                            className="px-1 bg-black text-white rounded-[0.5rem]"
                            buttonClassName="text-white"
                />
                {extraFilters ?? ''}
            </div>
            <div className="col-span-1 lg:text-right mt-2 lg:mt-0">
                {rightPanel}
            </div>
        </div>

        {fullWidthFilters ?? ''}

        <div className="overflow-x-auto">
            <table className="w-full table-auto text-left">
                <thead>
                <tr>
                    {headers.map(header => <th key={header.name}
                                               className={`${header.className ?? 'text-center'} ${extraClasses}`}>
                        <p className="text-blue-gray-900 dark:text-white antialiased font-sans text-sm font-normal leading-none opacity-70 w-max flex justify-center justify-self-center">
                            <>
                                {header.element ?? header.name}
                                {header.sortBy && options?.sorting ?
                                    <FontAwesomeIcon
                                        onClick={() => onChangeSort(sort_by === header.sortBy ? !sort_direction : false, header.sortBy ?? '')}
                                        className={`w-3 h-3 dark:text-gray-500 transition-transform cursor-pointer ml-4 ${sort_by === header.sortBy ? "text-black dark:text-white" : "text-gray-400 dark:text-gray-700"} ${sort_by === header.sortBy && sort_direction == 0 ? '' : 'rotate-180'}`}
                                        icon={solid("chevron-down")}/> : ''}
                            </>
                        </p>
                    </th>)}
                </tr>
                </thead>
                {children}
            </table>
        </div>
        {inProgress && <div
            className="absolute w-full h-full bg-white dark:bg-black top-0 left-0 opacity-30 flex items-center justify-center rounded-xl">
            <Loader width="w-32 h-32" colors="text-teal-600 fill-white"/>
        </div>}
        {!nodata ? pagination : <div className="text-center my-4">{t('admin.datatable.no_data')}</div>}
    </div>
}

export default memo(DataTable);
